import cx from 'classnames'
import { Text, ScreenReaderText } from '@cinch-labs/design-system'

import skeletonStyles from '../../skeleton.module.css'
import styles from './price.module.css'
import { sendDigitalDataEvent } from '@cinch-nx/shared-analytics'
import { useFinanceDrawerContext } from '../../../../context/finance-drawer-context'
import AdminFeeButton from '../admin-fee-button/admin-fee-button'
import { Env, readFromEnv } from '@cinch-nx/environments'

export const numberWithCommas = (x: string | number) => {
  if (typeof x === 'string') {
    return Number.parseInt(x.replace(/,/g, ''), 10).toLocaleString()
  }

  return Math.round(x).toLocaleString()
}

const penceToPounds = (n: number) => Math.round(n / 100)

const hasAValidQuote = (n: number | null | undefined): boolean =>
  typeof n === 'number' && n > 0

export interface PriceProps {
  price: number
  vehicleId: string
  shouldShowCarsOnOfferTag?: boolean
  shouldShowDepositContribution?: boolean
  discountInPence?: number
  quoteRegularPaymentInPence?: number | null
  quoteType?: string | null
  isPending?: boolean
  quoteTermMonths?: number | null
  onAdminFeesClick?: () => void
}

const Price = ({
  price,
  vehicleId,
  shouldShowCarsOnOfferTag = false,
  shouldShowDepositContribution = false,
  discountInPence,
  quoteRegularPaymentInPence,
  quoteType,
  isPending,
  quoteTermMonths,
  onAdminFeesClick,
}: PriceProps) => {
  const fullPrice = `£${numberWithCommas(price)}`
  const pricePerMonth = `£${numberWithCommas(
    penceToPounds(quoteRegularPaymentInPence ?? 0),
  )}`
  const wasPriceId = `${vehicleId}-was-price`
  const financeDrawerContext = useFinanceDrawerContext()

  const getWasPrice = () => {
    const discount = penceToPounds(discountInPence ?? 0)
    const wasPrice = `£${numberWithCommas(price + discount)}`

    return (
      <>
        Was <s>{wasPrice}</s>
      </>
    )
  }
  const shouldShowQuoteTermMonths =
    readFromEnv(Env.ShowQuoteTermMonths) === 'true'

  const renderPromotionalText = () => {
    let text: JSX.Element
    if (shouldShowDepositContribution) {
      text = <>Price includes deposit offer</>
    } else if (shouldShowCarsOnOfferTag) {
      text = getWasPrice()
    } else {
      return null
    }

    return (
      <Text
        element="span"
        id={wasPriceId}
        fontSizeFixed="xs"
        fontWeight="medium"
        aria-hidden={true}
        className={cx(styles['promotionText'], {
          [skeletonStyles['base']]: isPending,
          [skeletonStyles['active']]: isPending,
          [styles['rightAligned']]: shouldShowDepositContribution,
        })}
      >
        {text}
      </Text>
    )
  }

  const renderCashPrice = () => (
    <Text
      element="span"
      fontSizeFixed="l"
      fontWeight="semibold"
      aria-describedby={discountInPence ? wasPriceId : undefined}
      className={cx(styles['cashPrice'], {
        [skeletonStyles['base']]: isPending,
        [skeletonStyles['active']]: isPending,
      })}
    >
      <ScreenReaderText element="span" role="presentation">
        Full price.
      </ScreenReaderText>

      {`${fullPrice}`}
    </Text>
  )

  return (
    <div className={styles['marginTop']}>
      {renderPromotionalText()}

      <div>
        <div className={styles['priceContainer']}>
          {renderCashPrice()}

          {hasAValidQuote(quoteRegularPaymentInPence) && (
            <Text
              element="span"
              fontSizeFixed="l"
              fontWeight="semibold"
              className={cx(styles['pricePerMonth'], {
                [skeletonStyles['base']]: isPending,
                [skeletonStyles['active']]: isPending,
              })}
            >
              <ScreenReaderText
                element="span"
                role="presentation"
              >{`Price per month. ${quoteType}. `}</ScreenReaderText>

              {pricePerMonth}
            </Text>
          )}
        </div>
        <div className={styles['priceInfoContainer']}>
          <AdminFeeButton
            isPending={isPending}
            onAdminFeeClick={onAdminFeesClick}
          />
          {hasAValidQuote(quoteRegularPaymentInPence) &&
            (financeDrawerContext && quoteType ? (
              <Text
                element="button"
                className={styles['quoteTypeButton']}
                onClick={() => {
                  sendDigitalDataEvent({
                    event: {
                      name: 'click',
                      category: 'HP & PCP information',
                      label: 'info',
                      action: 'view',
                      value: quoteType,
                    },
                  })
                  financeDrawerContext.setIsShowingFinanceDrawer(true)
                }}
                fontSizeFixed="xs"
                aria-label={`Find more information on ${quoteType}`}
              >
                {shouldShowQuoteTermMonths && quoteTermMonths
                  ? ` ${quoteTermMonths} month ${quoteType.toUpperCase()}`
                  : ` /month ${quoteType.toUpperCase()}`}
              </Text>
            ) : (
              <Text element="small" fontSizeFixed="xs" aria-hidden={true}>
                {shouldShowQuoteTermMonths && quoteTermMonths
                  ? ` ${quoteTermMonths} month ${quoteType?.toUpperCase()}`
                  : ` /month ${quoteType?.toUpperCase()}`}
              </Text>
            ))}
        </div>
      </div>
    </div>
  )
}

export default Price
